import type { McpAttachment } from "@/lib/api";
import type { AgentMcpServer } from "@/lib/types";

/** The second line under a server in the dropdown: "stdio · all tools" or "connector · 3 of 13 tools". */
export function mcpSubtitle(server: Pick<AgentMcpServer, "kind" | "tools" | "knownTools">): string {
  if (server.tools.length === 0) return `${server.kind} · all tools`;
  return server.knownTools > 0 ? `${server.kind} · ${server.tools.length} of ${server.knownTools} tools` : `${server.kind} · ${server.tools.length} tool${server.tools.length === 1 ? "" : "s"}`;
}

/** What the agent dialog holds while editing: a server name and the tools of it chosen ([] = all). */
export interface McpChoice {
  server: string;
  tools: string[];
}

export const choicesOf = (servers: Pick<AgentMcpServer, "name" | "tools">[] | undefined): McpChoice[] =>
  (servers ?? []).map((entry) => ({ server: entry.name, tools: [...entry.tools] }));

/** The API's shape: a bare name when every tool is allowed, an object when only some. */
export const attachmentsOf = (choices: McpChoice[]): McpAttachment[] =>
  choices.map((choice) => (choice.tools.length ? { server: choice.server, tools: choice.tools } : choice.server));

/** True when two selections attach the same servers with the same tools (order aside for tools, not servers). */
export function sameChoices(a: McpChoice[], b: McpChoice[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((choice, index) => {
    const other = b[index];
    return other !== undefined && other.server === choice.server && [...other.tools].sort().join(",") === [...choice.tools].sort().join(",");
  });
}

/**
 * `KEY=value` per line (env) or `Name: value` per line (headers), blank lines and
 * `#` comments ignored. Returns the pairs, or the 1-based number of the first line
 * that is neither — a line number and never the line, since it holds a secret.
 */
export function parsePairs(text: string, separator: "=" | ":"): { pairs: Record<string, string> } | { badLine: number } {
  const pairs: Record<string, string> = {};
  const lines = text.split(/\r?\n/);
  for (let index = 0; index < lines.length; index++) {
    const line = lines[index]!.trim();
    if (!line || line.startsWith("#")) continue;
    const at = line.indexOf(separator);
    if (at < 1) return { badLine: index + 1 };
    pairs[line.slice(0, at).trim()] = line.slice(at + 1).trim();
  }
  return { pairs };
}

/** Tool names typed as a list: separated by spaces, commas or new lines. */
export const parseToolNames = (text: string): string[] => [...new Set(text.split(/[\s,]+/).filter(Boolean))];
